import { supabase } from "../lib/supabase";
import type { MoneybirdContactPreview } from "../utils/moneybirdContactNameMatch";

interface MoneybirdResponse {
  success?: boolean;
  error?: string;
  message?: string;
  moneybird_invoice_id?: string;
  moneybird_invoice_url?: string;
  results?: {
    success: number;
    failed: number;
    updated: number;
    errors: string[];
  };
}

/**
 * Create an invoice in Moneybird
 */
export const createMoneybirdInvoice = async (invoiceId: number): Promise<{
  data: MoneybirdResponse | null;
  error: string | null;
}> => {
  try {
    const { data, error } = await supabase.functions.invoke(
      'create-moneybird-invoice',
      {
        body: { invoice_id: invoiceId },
      }
    );

    if (error) {
      console.error('Error calling create-moneybird-invoice function:', error);
      return {
        data: null,
        error: error.message || 'Failed to create Moneybird invoice',
      };
    }

    return {
      data: data as MoneybirdResponse,
      error: null,
    };
  } catch (error) {
    console.error('Unexpected error calling create-moneybird-invoice:', error);
    return {
      data: null,
      error: error instanceof Error ? error.message : 'Unknown error',
    };
  }
};

/**
 * Update an existing invoice in Moneybird
 */
export const updateMoneybirdInvoice = async (invoiceId: number): Promise<{
  data: MoneybirdResponse | null;
  error: string | null;
}> => {
  try {
    const { data, error } = await supabase.functions.invoke(
      'update-moneybird-invoice',
      {
        body: { invoice_id: invoiceId },
      }
    );

    if (error) {
      console.error('Error calling update-moneybird-invoice function:', error);
      return {
        data: null,
        error: error.message || 'Failed to update Moneybird invoice',
      };
    }

    return {
      data: data as MoneybirdResponse,
      error: null,
    };
  } catch (error) {
    console.error('Unexpected error calling update-moneybird-invoice:', error);
    return {
      data: null,
      error: error instanceof Error ? error.message : 'Unknown error',
    };
  }
};

/**
 * Sync payment status from Moneybird for a specific invoice
 */
export const syncMoneybirdStatus = async (invoiceId: number): Promise<{
  data: MoneybirdResponse | null;
  error: string | null;
}> => {
  try {
    const { data, error } = await supabase.functions.invoke(
      'sync-moneybird-status',
      {
        body: { invoice_id: invoiceId },
      }
    );

    if (error) {
      console.error('Error calling sync-moneybird-status function:', error);
      return {
        data: null,
        error: error.message || 'Failed to sync Moneybird status',
      };
    }

    return {
      data: data as MoneybirdResponse,
      error: null,
    };
  } catch (error) {
    console.error('Unexpected error calling sync-moneybird-status:', error);
    return {
      data: null,
      error: error instanceof Error ? error.message : 'Unknown error',
    };
  }
};

/**
 * Sync payment status from Moneybird for all synced invoices
 */
export const syncAllMoneybirdStatus = async (): Promise<{
  data: MoneybirdResponse | null;
  error: string | null;
}> => {
  try {
    const { data, error } = await supabase.functions.invoke(
      'sync-moneybird-status',
      {
        body: { sync_all: true },
      }
    );

    if (error) {
      console.error('Error calling sync-moneybird-status function:', error);
      return {
        data: null,
        error: error.message || 'Failed to sync all Moneybird statuses',
      };
    }

    return {
      data: data as MoneybirdResponse,
      error: null,
    };
  } catch (error) {
    console.error('Unexpected error calling sync-moneybird-status:', error);
    return {
      data: null,
      error: error instanceof Error ? error.message : 'Unknown error',
    };
  }
};

export type AdminMoneybirdContactsResponse = {
  contacts: MoneybirdContactPreview[];
  truncated: boolean;
  administration_id: string;
};

/**
 * Admin-only: paginated list of Moneybird contacts for linking a company (edge function enforces admin + API key).
 */
export const fetchAdminMoneybirdContacts = async (): Promise<{
  data: AdminMoneybirdContactsResponse | null;
  error: string | null;
}> => {
  try {
    const { data, error } = await supabase.functions.invoke(
      "admin-moneybird-contacts",
      { body: {} }
    );

    if (error) {
      console.error("Error calling admin-moneybird-contacts:", error);
      return {
        data: null,
        error: error.message || "Failed to load Moneybird contacts",
      };
    }

    const payload = data as AdminMoneybirdContactsResponse & { error?: string };
    if (payload?.error) {
      return { data: null, error: payload.error };
    }
    if (!payload?.contacts || !Array.isArray(payload.contacts)) {
      return { data: null, error: "Invalid response from server" };
    }

    return {
      data: {
        contacts: payload.contacts,
        truncated: Boolean(payload.truncated),
        administration_id: String(payload.administration_id ?? ""),
      },
      error: null,
    };
  } catch (error) {
    console.error("Unexpected error in fetchAdminMoneybirdContacts:", error);
    return {
      data: null,
      error: error instanceof Error ? error.message : "Unknown error",
    };
  }
};

export type AdminMoneybirdCompanyContactResult = {
  action: "linked_existing" | "created";
  contact_id: string;
  message: string;
};

/**
 * Admin-only: find Moneybird contact by EFF customer id or exact company name, else create from company row and link.
 */
export const adminMoneybirdUpsertContactFromCompany = async (
  companyId: number
): Promise<{
  data: AdminMoneybirdCompanyContactResult | null;
  error: string | null;
}> => {
  try {
    const { data, error } = await supabase.functions.invoke(
      "admin-moneybird-company-contact",
      { body: { company_id: companyId } }
    );

    const body = data as
      | (AdminMoneybirdCompanyContactResult & { error?: string })
      | null;

    if (body && typeof body.error === "string" && body.error) {
      return { data: null, error: body.error };
    }

    if (error) {
      return {
        data: null,
        error: error.message || "Failed to sync Moneybird contact",
      };
    }

    if (
      !body ||
      typeof body.contact_id !== "string" ||
      (body.action !== "linked_existing" && body.action !== "created")
    ) {
      return { data: null, error: "Invalid response from server" };
    }

    return {
      data: {
        action: body.action,
        contact_id: body.contact_id,
        message: body.message ?? "Done",
      },
      error: null,
    };
  } catch (e) {
    console.error("adminMoneybirdUpsertContactFromCompany:", e);
    return {
      data: null,
      error: e instanceof Error ? e.message : "Unknown error",
    };
  }
};